import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ToolCard } from "@/components/energy/tool";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "@/data/site";

const TOOLS = [
  { to: "/energy/engine", title: "BNM Energy Engine™", desc: "Tell us what you need to power — get a complete preliminary energy system plan.", testid: "tool-card-engine" },
  { to: "/solar", title: "Solar Energy Calculator", desc: "The full solar design tool with bill of materials and PDF report.", testid: "tool-card-solar" },
  { to: "/energy/compare", title: "Generator vs Solar Cost Calculator", desc: "Compare your generator's real fuel and maintenance cost against solar.", testid: "tool-card-compare" },
  { to: "/energy/outage", title: "Power Outage Impact Calculator", desc: "Estimate what outages cost your business every month and year.", testid: "tool-card-outage" },
  { to: "/energy/presets/business", title: "Solar for My Business", desc: "Barbing salon, cold store, café, printing — instant business energy profiles.", testid: "tool-card-business" },
  { to: "/energy/presets/agriculture", title: "Solar for Agriculture", desc: "Irrigation, boreholes, poultry, cold storage and agro-processing.", testid: "tool-card-agriculture" },
  { to: "/energy/presets/schools", title: "School Energy Assessment", desc: "Classrooms, ICT labs and evening study — sized for learning.", testid: "tool-card-schools" },
  { to: "/energy/presets/clinics", title: "Clinic & Health Facility Assessment", desc: "Critical-load-first planning for medical refrigeration and lighting.", testid: "tool-card-clinics" },
  { to: "/energy/battery", title: "Battery Health & Runtime Calculator", desc: "Check usable capacity and expected runtime of your battery bank.", testid: "tool-card-battery" },
  { to: "/energy/safety", title: "Appliance & Power Safety Assistant", desc: "Surge, voltage and protection guidance for any appliance.", testid: "tool-card-safety" },
  { to: "/energy/emergency", title: "Solar Emergency Assistant", desc: "My solar system isn't working — guided safe troubleshooting and support.", testid: "tool-card-emergency" },
  { to: "/energy/map", title: "BNM Energy Access Map", desc: "Communities, schools and clinics requesting energy across Sierra Leone.", testid: "tool-card-map" },
  { to: "/energy/financing", title: "Make Solar Affordable", desc: "Illustrative payment plans — deposit and monthly estimates.", testid: "tool-card-financing" },
  { to: "/energy/learn", title: "Energy Knowledge Center", desc: "Plain-language guides: watts, batteries, inverters, maintenance and more.", testid: "tool-card-learn" },
];

export default function Energy() {
  return (
    <>
      <Seo
        title="BNM Energy Intelligence & Solutions — Energy Planning Tools for Sierra Leone"
        description="BNM Energy Intelligence & Solutions: free energy planning tools — Energy Engine, solar calculator, generator cost comparison, outage impact, battery tools, business, agriculture, school and clinic assessments."
      />
      <PageHero
        testid="energy-hero"
        eyebrow="BNM Energy Intelligence & Solutions · Designed for Salone"
        lines={["Your Energy.", "Our Engineering."]}
        lead="Tell us what you need to power. Our tools help you understand the energy system required — with transparent assumptions, Sierra Leone pricing in Leones, and honest preliminary engineering."
      >
        <Reveal delay={0.4}>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/energy/engine" data-testid="energy-start-cta"
              className="inline-flex items-center gap-2 rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian">
              Start My Energy Assessment
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/energy/carbon-free-generator" data-testid="energy-cfg-cta"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-7 py-3.5 font-semibold text-white transition-colors hover:border-volt/60 hover:text-volt">
              Explore Carbon-Free Generator
            </Link>
          </div>
        </Reveal>
      </PageHero>

      <section data-testid="energy-tools" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="The Toolkit"
            title="Fourteen tools. One goal: the right energy system."
            lead="Free preliminary assessments for homes, businesses, farms, schools and clinics — in Leones, for Sierra Leone conditions."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {TOOLS.map((t, i) => (
              <Reveal key={t.to} delay={(i % 3) * 0.06}>
                <ToolCard {...t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="energy-cfg-band" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <SectionHead
              dark
              eyebrow="BNM Technology Option"
              title="The Carbon-Free Generator, inside your energy plan."
              lead="BNM's flagship carbon-free generation technology — a 500W prototype today, evolving toward a 5kVA hybrid system — is part of the Energy Intelligence platform as a future power option for homes, schools, clinics and enterprises."
            />
            <Reveal delay={0.1}>
              <figure className="rounded-lg border border-volt/25 overflow-hidden">
                <img src={ASSETS.generator} alt="BNM Carbon Free Generator 500W prototype" className="w-full h-72 object-cover" loading="lazy" width="1024" height="768" />
              </figure>
              <Link to="/energy/carbon-free-generator" data-testid="energy-cfg-band-cta"
                className="mt-5 inline-flex items-center gap-2 font-semibold text-volt hover:text-volt-soft transition-colors">
                Learn about the Carbon-Free Generator
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title="Your Energy. Our Engineering."
        text="Tell BNM what you need to power. Our technology will help you understand the energy system required."
        primary={{ label: "Start My Energy Assessment", to: "/energy/engine", testid: "energy-final-cta" }}
        secondary={{ label: "Request BNM Engineering Support", to: "/energy/emergency", testid: "energy-support-cta" }}
        testid="energy-final-band"
      />
    </>
  );
}
